import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { formatDate } from "../../utils/formatDate";

/**
 * Return detail.
 *
 * Provider-neutral: it renders whatever the row actually carries and omits the
 * rest, rather than branching on `oms_provider`. That matters because the two
 * pipelines fill genuinely different subsets of the shared model — Pipe17 fills
 * the money/receipts/causes fields and Shopify fills the reverse-fulfillment
 * ones — and a section hidden when empty reads correctly for BOTH, while an
 * always-rendered section reads as "this data is missing" for one of them.
 */

const LABEL_SX = {
  fontSize: 12,
  fontFamily: "Poppins",
  fontWeight: 600,
  color: "#344054",
};
const VALUE_SX = {
  fontSize: 12,
  fontFamily: "Poppins",
  fontWeight: 400,
  color: "#667085",
  wordBreak: "break-word",
};

const Field = ({ label, value, xs = 6, sm = 4 }) => (
  <Grid item xs={xs} sm={sm}>
    <Typography sx={LABEL_SX}>{label}</Typography>
    <Typography sx={VALUE_SX}>
      {value === 0 ? 0 : value || "--"}
    </Typography>
  </Grid>
);

const SectionTitle = ({ children }) => (
  <Typography
    sx={{
      fontSize: 13,
      fontFamily: "Poppins",
      fontWeight: 600,
      color: "#101828",
      mt: 2,
      mb: 1,
    }}
  >
    {children}
  </Typography>
);

/** Format for display only — never re-derive. The stored String is exactly what
 *  the provider reported, and recomputing a refund total here would let the
 *  portal quietly disagree with the provider's own figure. */
const money = (amount, currency) => {
  if (amount === null || amount === undefined || amount === "") return "--";
  const n = Number(amount);
  if (Number.isNaN(n)) return String(amount);
  return `${currency ? `${currency} ` : ""}${n.toFixed(2)}`;
};

const addressLines = (a) => {
  if (!a) return null;
  return [
    a.name,
    a.company,
    a.address1,
    a.address2,
    [a.city, a.province_code || a.province, a.zip].filter(Boolean).join(", "),
    a.country,
    a.phone,
    a.email,
  ].filter(Boolean);
};

const ReturnViewModal = ({ data, open, onClose }) => {
  if (!data) return null;

  const lineItems = data.line_items || [];
  const requested = data.requested_items || [];
  const shipments = data.return_shipments || [];
  const receipts = data.receipts || [];
  const address = addressLines(data.shipping_address);

  // Reason lives on `requested_items` and the sku on `line_items`; for Pipe17
  // both come off the same source line so they share an id, which is what makes
  // this join valid. For Shopify there IS no join (see return.model.js) and the
  // lookup simply misses, leaving the reason column blank rather than wrong.
  const reasonFor = (item) => {
    const match = requested.find((r) => r.id && r.id === item.id);
    if (!match) return null;
    return [match.cause, match.customer_note || match.return_reason_note]
      .filter(Boolean)
      .join(" — ");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <DialogTitle sx={{ fontFamily: "Poppins" }}>
          Return {data.name || data.external_id}
        </DialogTitle>
        <IconButton onClick={onClose} sx={{ mr: 2 }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography sx={LABEL_SX}>Status</Typography>
            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
              <Chip
                label={data.status || "--"}
                size="small"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "11px",
                  height: "22px",
                }}
              />
              {/* The provider's raw value, shown next to the display label so an
                  integration question can be answered without a DB query. */}
              {data.provider_status ? (
                <Typography sx={{ ...VALUE_SX, fontSize: 11 }}>
                  ({data.provider_status})
                </Typography>
              ) : null}
            </Box>
          </Grid>
          <Field label="Source" value={data.oms_provider} />
          {/* Stored on the return, resolved from its order — see the Returns
              table's CHANNEL column. */}
          <Field label="Channel" value={data.channel} />
          <Field label="Order" value={data.order_name} />
          <Field label="Total Quantity" value={data.total_quantity} />
          <Field label="Exchange" value={data.is_exchange ? "Yes" : "No"} />
          <Field label="Gift" value={data.is_gift ? "Yes" : "No"} />
          <Field
            label="Requested"
            value={data.requested_at ? formatDate(data.requested_at, "") : null}
          />
          <Field
            label="Expected Arrival"
            value={
              data.expected_arrival_date
                ? formatDate(data.expected_arrival_date, "")
                : null
            }
          />
          <Field
            label="Arrived"
            value={data.arrived_at ? formatDate(data.arrived_at, "") : null}
          />
          <Field
            label="Refunded"
            value={data.refunded_at ? formatDate(data.refunded_at, "") : null}
          />
          <Field
            label="Closed"
            value={data.closed_at ? formatDate(data.closed_at, "") : null}
          />
          <Field label="Provider ID" value={data.external_id} />
        </Grid>

        {data.exceptions?.length ? (
          <>
            <SectionTitle>Exceptions</SectionTitle>
            <Box display="flex" gap={1} flexWrap="wrap">
              {data.exceptions.map((e) => (
                <Chip
                  key={e}
                  label={e}
                  size="small"
                  sx={{
                    backgroundColor: "#FEF3F2",
                    color: "#B42318",
                    fontFamily: "Poppins",
                    fontSize: "11px",
                  }}
                />
              ))}
            </Box>
          </>
        ) : null}

        {lineItems.length ? (
          <>
            <SectionTitle>Items</SectionTitle>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    "SKU",
                    "Item",
                    "Qty",
                    "Received",
                    "Damaged",
                    "Refunded",
                    "Action",
                    "Reason",
                    "Refund",
                  ].map((h) => (
                    <TableCell key={h} sx={LABEL_SX}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.map((l, i) => (
                  <TableRow key={l.id || i}>
                    <TableCell sx={VALUE_SX}>{l.sku || "--"}</TableCell>
                    <TableCell sx={VALUE_SX}>{l.title || "--"}</TableCell>
                    <TableCell sx={VALUE_SX}>{l.quantity ?? "--"}</TableCell>
                    <TableCell sx={VALUE_SX}>
                      {l.quantity_received ?? "--"}
                    </TableCell>
                    <TableCell sx={VALUE_SX}>
                      {l.quantity_damaged ?? "--"}
                    </TableCell>
                    <TableCell sx={VALUE_SX}>
                      {l.quantity_refunded ?? "--"}
                    </TableCell>
                    <TableCell sx={VALUE_SX}>{l.action || "--"}</TableCell>
                    <TableCell sx={VALUE_SX}>{reasonFor(l) || "--"}</TableCell>
                    <TableCell sx={VALUE_SX}>
                      {money(l.item_total, data.currency)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        ) : null}

        {/* Rendered only when the provider actually reports money. Shopify's
            Return connection exposes none of it, so an always-on Financials
            block would read as "the refund is zero" on every Shopify return. */}
        {[data.subtotal, data.total, data.tax, data.estimated_total].some(
          (v) => v !== null && v !== undefined && v !== ""
        ) ? (
          <>
            <SectionTitle>Financials</SectionTitle>
            <Grid container spacing={2}>
              <Field label="Subtotal" value={money(data.subtotal, data.currency)} />
              <Field label="Tax" value={money(data.tax, data.currency)} />
              <Field label="Discount" value={money(data.discount, data.currency)} />
              <Field
                label="Restocking Fee"
                value={money(data.restocking_fee, data.currency)}
              />
              <Field
                label="Shipping Refund"
                value={money(data.shipping_refund, data.currency)}
              />
              <Field
                label="Label Fee"
                value={money(data.shipping_label_fee, data.currency)}
              />
              <Field
                label="Estimated Total"
                value={money(data.estimated_total, data.currency)}
              />
              <Field label="Total" value={money(data.total, data.currency)} />
            </Grid>
          </>
        ) : null}

        {shipments.length ? (
          <>
            <SectionTitle>Return Shipping</SectionTitle>
            <Grid container spacing={2}>
              {shipments.map((s, i) => (
                <React.Fragment key={s.reverse_delivery_id || i}>
                  <Field label="Carrier" value={s.carrier_name} />
                  <Field
                    label="Service"
                    value={s.shipping_service || data.shipping_service}
                  />
                  <Field label="Tracking" value={s.tracking_number} />
                  {s.tracking_url ? (
                    <Grid item xs={6} sm={4}>
                      <Typography sx={LABEL_SX}>Track</Typography>
                      <Link
                        href={s.tracking_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={VALUE_SX}
                      >
                        Open
                      </Link>
                    </Grid>
                  ) : null}
                  {s.label_url ? (
                    <Grid item xs={6} sm={4}>
                      <Typography sx={LABEL_SX}>Label</Typography>
                      <Link
                        href={s.label_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={VALUE_SX}
                      >
                        Download
                      </Link>
                    </Grid>
                  ) : null}
                </React.Fragment>
              ))}
            </Grid>
          </>
        ) : null}

        {receipts.length ? (
          <>
            {/* What the warehouse actually booked in, as distinct from what the
                customer declared. The two disagreeing is the whole point of
                showing both. */}
            <SectionTitle>Receipts</SectionTitle>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Receipt", "Arrived", "SKU", "Qty", "Damaged"].map((h) => (
                    <TableCell key={h} sx={LABEL_SX}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {receipts.flatMap((r, ri) =>
                  (r.line_items || []).map((l, li) => (
                    <TableRow key={`${ri}-${li}`}>
                      <TableCell sx={VALUE_SX}>
                        {r.ext_receipt_id || "--"}
                      </TableCell>
                      <TableCell sx={VALUE_SX}>
                        {r.actual_arrival_date
                          ? formatDate(r.actual_arrival_date, "")
                          : "--"}
                      </TableCell>
                      <TableCell sx={VALUE_SX}>{l.sku || "--"}</TableCell>
                      <TableCell sx={VALUE_SX}>{l.quantity ?? "--"}</TableCell>
                      <TableCell sx={VALUE_SX}>
                        {l.quantity_damaged ?? "--"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </>
        ) : null}

        {address?.length ? (
          <>
            <SectionTitle>Return From</SectionTitle>
            <Typography sx={VALUE_SX}>{address.join(", ")}</Typography>
          </>
        ) : null}

        {data.customer_notes || data.notes ? (
          <>
            <SectionTitle>Notes</SectionTitle>
            <Grid container spacing={2}>
              <Field
                label="Customer"
                value={data.customer_notes}
                xs={12}
                sm={6}
              />
              <Field label="Merchant" value={data.notes} xs={12} sm={6} />
            </Grid>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default ReturnViewModal;
